import { useQuery } from '@tanstack/react-query';
import { type ReactNode, createContext, useContext, useMemo } from 'react';
import type { SupabaseClientLike } from './people';
import type { RoleUIElement } from './roles';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface RoleUIContextValue {
  elementMap: Map<string, { visibility: string; mode: string }>;
}

export const RoleUIContext = createContext<RoleUIContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
//
// Fetches role_ui_elements for the current user via RLS — the DB policy
// filters to the user's own role automatically. No role_id needed on the client.
// ---------------------------------------------------------------------------

interface RoleUIProviderProps {
  children: ReactNode;
  supabase: SupabaseClientLike;
  tenantId: string;
  /** Arbitrary cache key to re-fetch when role changes (e.g. role_name). */
  roleCacheKey?: string | null;
}

export function RoleUIProvider({
  children,
  supabase,
  tenantId,
  roleCacheKey,
}: RoleUIProviderProps) {
  const { data } = useQuery({
    queryKey: ['role_ui_elements', tenantId, roleCacheKey],
    queryFn: async (): Promise<RoleUIElement[]> => {
      const { data: rows, error } = await (supabase as any)
        .from('role_ui_elements')
        .select('element_key, visibility, mode')
        .eq('tenant_id', tenantId);
      if (error) throw error;
      return (rows ?? []) as RoleUIElement[];
    },
    enabled: Boolean(tenantId),
    staleTime: 5 * 60 * 1000,
  });

  const elementMap = useMemo(() => {
    const m = new Map<string, { visibility: string; mode: string }>();
    for (const row of data ?? []) m.set(row.element_key, row);
    return m;
  }, [data]);

  return <RoleUIContext.Provider value={{ elementMap }}>{children}</RoleUIContext.Provider>;
}

export function useRoleUIContext(): RoleUIContextValue | null {
  return useContext(RoleUIContext);
}
